const KEY_PREFIX = 'quoruum';

// Add new persisted values here; the value type is the single source of truth for each key.
export interface OrgStorageSchema {
  'hotels.recent': string[];
}

export interface GlobalStorageSchema {
  selectedOrgId: string;
}

export type OrgStorageKey = keyof OrgStorageSchema;
export type GlobalStorageKey = keyof GlobalStorageSchema;

export const OrgStorageKeys = {
  RecentHotels: 'hotels.recent',
} as const satisfies Record<string, OrgStorageKey>;

export const GlobalStorageKeys = {
  SelectedOrgId: 'selectedOrgId',
} as const satisfies Record<string, GlobalStorageKey>;

// Which org is selected is itself global; everything else is namespaced per org.
function globalKey(key: GlobalStorageKey): string {
  return `${KEY_PREFIX}.${key}`;
}

function orgKey(orgId: string, key: OrgStorageKey): string {
  return `${KEY_PREFIX}.org.${encodeURIComponent(orgId)}.${key}`;
}

function read<T>(storageKey: string): T | null {
  try {
    const raw = window.localStorage.getItem(storageKey);
    return raw === null ? null : (JSON.parse(raw) as T);
  } catch {
    return null;
  }
}

function write(storageKey: string, value: unknown): void {
  try {
    window.localStorage.setItem(storageKey, JSON.stringify(value));
  } catch {
    // localStorage unavailable or full; persistence is best-effort.
  }
}

function remove(storageKey: string): void {
  try {
    window.localStorage.removeItem(storageKey);
  } catch {
    // best-effort
  }
}

// Values are untyped at runtime, so callers must validate what they read.
export const globalStorage = {
  get<K extends GlobalStorageKey>(key: K): GlobalStorageSchema[K] | null {
    return read<GlobalStorageSchema[K]>(globalKey(key));
  },
  set<K extends GlobalStorageKey>(key: K, value: GlobalStorageSchema[K]): void {
    write(globalKey(key), value);
  },
  remove(key: GlobalStorageKey): void {
    remove(globalKey(key));
  },
};

// Storage bound to one organization so switching orgs never leaks data between them.
export interface OrgStorage {
  get<K extends OrgStorageKey>(key: K): OrgStorageSchema[K] | null;
  set<K extends OrgStorageKey>(key: K, value: OrgStorageSchema[K]): void;
  remove(key: OrgStorageKey): void;
}

export function createOrgStorage(orgId: string): OrgStorage {
  return {
    get: (key) => read(orgKey(orgId, key)),
    set: (key, value) => write(orgKey(orgId, key), value),
    remove: (key) => remove(orgKey(orgId, key)),
  };
}
